import { Observable } from 'rxjs/Rx'
import { combineEpics } from 'redux-observable'
import { parseDeliveryPayload, decorateDeliveryEntity } from 'store/utils/delivery'
import {
  estimate,
  create,
  get,
  all,
  reference,
  transition,
  events,
  scheduled,
  search,
  profitability,
  createProfitability,
  updateProfitability,
  deleteProfitability,
} from 'store/services/delivery'
import { alertSuccess, alertError } from 'store/ducks/alert'
import * as authenticationDuck from 'store/ducks/authentication'
import { intl } from 'i18n'
import PropTypes from 'prop-types'
import { decorateDeliveryResponsentity, parseDeliveryUpdatePayload } from '../utils/delivery'
import { applyCourierTransition, modify, operatorCourierTransition, validation } from '../services/delivery'
import { Delivery } from 'interfaces/delivery'
import { DeliveryQuotation } from 'interfaces/deliveryQuotation'
import { DeliveryEvent } from 'interfaces/deliveryEvent'
import { CompanyCourier } from 'interfaces/companycourier'
import { AnyAction } from '@reduxjs/toolkit'
import { of } from 'rxjs/observable/of'

// Actions
export const MOVE_STEP = 'delivery/MOVE_STEP'

export const ESTIMATION_REQUEST = 'delivery/ESTIMATION_REQUEST'
export const ESTIMATION_SUCCESS = 'delivery/ESTIMATION_SUCCESS'
export const ESTIMATION_FAILURE = 'delivery/ESTIMATION_FAILURE'
export const ESTIMATION_RESET = 'delivery/ESTIMATION_RESET'

export const CREATION_REQUEST = 'delivery/CREATION_REQUEST'
export const CREATION_SUCCESS = 'delivery/CREATION_SUCCESS'
export const CREATION_FAILURE = 'delivery/CREATION_FAILURE'
export const CREATION_RESET = 'delivery/CREATION_RESET'

export const CREATE_PROFITABILITY_REQUEST = 'delivery/CREATE_PROFITABILITY_REQUEST'
export const UPDATE_PROFITABILITY_REQUEST = 'delivery/UPDATE_PROFITABILITY_REQUEST'
export const DELETE_PROFITABILITY_REQUEST = 'delivery/DELETE_PROFITABILITY_REQUEST'
export const CREATE_PROFITABILITY_SUCCESS = 'delivery/CREATE_PROFITABILITY_SUCCESS'
export const CREATE_PROFITABILITY_FAILURE = 'delivery/CREATE_PROFITABILITY_FAILURE'

export const DELIVERY_UPDATE_REQUEST = 'delivery/DELIVERY_UPDATE_REQUEST'
export const DELIVERY_UPDATE_SUCCESS = 'delivery/DELIVERY_UPDATE_SUCCESS'
export const DELIVERY_UPDATE_FAILURE = 'delivery/DELIVERY_UPDATE_FAILURE'
export const DELIVERY_UPDATE_RESET = 'delivery/DELIVERY_UPDATE_RESET'

export const DELIVERY_MODIFY_REQUEST = 'delivery/DELIVERY_MODIFY_REQUEST'
export const DELIVERY_MODIFY_SUCCESS = 'delivery/DELIVERY_MODIFY_SUCCESS'
export const DELIVERY_MODIFY_FAILURE = 'delivery/DELIVERY_MODIFY_FAILURE'
export const DELIVERY_MODIFY_RESET = 'delivery/DELIVERY_MODIFY_RESET'

export const RETRY_REQUEST = 'delivery/RETRY_REQUEST'
export const RETRY_SUCCESS = 'delivery/RETRY_SUCCESS'
export const RETRY_FAILURE = 'delivery/RETRY_FAILURE'

export const FETCH_REQUEST = 'delivery/FETCH_REQUEST'
export const FETCH_SUCCESS = 'delivery/FETCH_SUCCESS'
export const FETCH_FAILURE = 'delivery/FETCH_FAILURE'

export const FETCH_REFERENCE_REQUEST = 'delivery/FETCH_REFERENCE_REQUEST'

export const FETCH_ALL_REQUEST = 'delivery/FETCH_ALL_REQUEST'
export const FETCH_ALL_SUCCESS = 'delivery/FETCH_ALL_SUCCESS'
export const FETCH_ALL_FAILURE = 'delivery/FETCH_ALL_FAILURE'

export const FETCH_PROFITABILITY_REQUEST = 'delivery/FETCH_PROFITABILITY_REQUEST'
export const FETCH_PROFITABILITY_SUCCESS = 'delivery/FETCH_PROFITABILITY_SUCCESS'
export const FETCH_PROFITABILITY_FAILURE = 'delivery/FETCH_PROFITABILITY_FAILURE'

export const FETCH_SEARCH_REQUEST = 'delivery/FETCH_SEARCH_REQUEST'
export const FETCH_SEARCH_SUCCESS = 'delivery/FETCH_SEARCH_SUCCESS'
export const FETCH_SEARCH_FAILURE = 'delivery/FETCH_SEARCH_FAILURE'

export const FETCH_SCHEDULED_REQUEST = 'delivery/FETCH_SCHEDULED_REQUEST'
export const FETCH_SCHEDULED_SUCCESS = 'delivery/FETCH_SCHEDULED_SUCCESS'
export const FETCH_SCHEDULED_FAILURE = 'delivery/FETCH_SCHEDULED_FAILURE'

export const CANCEL_REQUEST = 'delivery/CANCEL_REQUEST'
export const CANCEL_SUCCESS = 'delivery/CANCEL_SUCCESS'

export const APPLY_REQUEST = 'delivery/APPLY_REQUEST'
export const APPLY_SUCCESS = 'delivery/APPLY_SUCCESS'
export const APPLY_FAILURE = 'delivery/APPLY_FAILURE'

export const CONFIRM_REQUEST = 'delivery/CONFIRM_REQUEST'
export const CONFIRM_SUCCESS = 'delivery/CONFIRM_SUCCESS'
export const CONFIRM_FAILURE = 'delivery/CONFIRM_FAILURE'

export const REJECT_REQUEST = 'delivery/REJECT_REQUEST'
export const REJECT_SUCCESS = 'delivery/REJECT_SUCCESS'
export const REJECT_FAILURE = 'delivery/REJECT_FAILURE'

export const EVENTS_REQUEST = 'delivery/EVENTS_REQUEST'
export const EVENTS_SUCCESS = 'delivery/EVENTS_SUCCESS'
export const EVENTS_FAILURE = 'delivery/EVENTS_FAILURE'

export const APPLY_MODAL_OPEN = 'delivery/APPLY_MODAL_OPEN'
export const APPLY_MODAL_CLOSE = 'delivery/APPLY_MODAL_CLOSE'

export const CURRENT_RESET = 'delivery/CURRENT_RESET'
export const DELIVERY_MODIFIED_VALUES = 'delivery/DELIVERY_MODIFIED_VALUES'

export const DELIVERY_COURIER_TRANSITION_REQUEST = 'delivery/DELIVERY_COURIER_TRANSITION_REQUEST'
export const DELIVERY_COURIER_TRANSITION_SUCCESS = 'delivery/DELIVERY_COURIER_TRANSITION_SUCCESS'
export const DELIVERY_COURIER_TRANSITION_FAILURE = 'delivery/DELIVERY_COURIER_TRANSITION_FAILURE'

export const APPLY_COURIER_TRANSITION_REQUEST = 'operations/APPLY_COURIER_TRANSITION_REQUEST'
export const APPLY_COURIER_TRANSITION_SUCCESS = 'operations/APPLY_COURIER_TRANSITION_SUCCESS'
export const APPLY_COURIER_TRANSITION_FAILURE = 'operations/APPLY_COURIER_TRANSITION_FAILURE'

type DeliveryResponse<T> = {
  loading: boolean
  violations: string[]
  entity: T
}

export type DecoratedDeliveryEntity = Delivery & {
  roles: string[]
  events: DeliveryEvent[]
}

export type Estimation = DeliveryResponse<DeliveryQuotation> & {
  estimated: boolean
}

export type Creation = DeliveryResponse<Delivery> & {
  created: boolean
}

export type Update = DeliveryResponse<Delivery> & {
  validated: boolean
}

// FIXME: do proper typing of this entity
export type DeliveryElementEntity<T> = {
  entities: Record<number, T>
  entity: T[]
  loading: boolean
}

export type ProfitabilityDelivery = {
  id: string | number
  startDate: string
  endDate: string
  vehicle: ProfitabilityVehicle
  courier: CompanyCourier
  company: string
  price: number
  otherFees: number
  basketPrimeNumber: number
  nightPrimeNumber: number
  mileage: number
  deadheadMileage: number
  comment: string
  startingPoint: string
  arrivalPoint: string
  createdAt: Date
}

export type ProfitabilityVehicle = {
  key: string
  price: number
  totalCost: number
  cost: number
  mileage: number
  margin: number
  licenseNumber: string
  landingPoint: string
  type: string
  couriers: CompanyCourier[]
  balance: number
}

export type DeliveryState = {
  step: number
  estimation: Estimation
  creation: Creation
  update: Update
  retry: {
    loading: boolean
    retried: boolean
  }
  list: DeliveryElementEntity<DecoratedDeliveryEntity> & {
    total: number
  }
  profitability: {
    entities: ProfitabilityDelivery[]
    loading: boolean
  }
  scheduled: DeliveryElementEntity<unknown> & {
    total: number
  }
  search: DeliveryElementEntity<unknown> & {
    total: number
  }
  current:
    | {
        loading?: boolean
      }
    | Delivery
}

// Reducer
const INITIAL_STATE: DeliveryState = {
  step: 0,
  estimation: {
    loading: false,
    estimated: false,
    entity: {},
    violations: [],
  },
  update: {
    loading: false,
    validated: false,
    entity: {},
    violations: [],
  },
  creation: {
    loading: false,
    created: false,
    entity: {},
    violations: [],
  },
  retry: {
    loading: false,
    retried: false,
  },
  list: {
    loading: false,
    entity: [],
    entities: {},
    total: null,
  },
  profitability: {
    loading: false,
    entities: [],
  },
  scheduled: {
    loading: false,
    entity: [],
    entities: {},
  },
  search: {
    loading: false,
    entity: [],
    entities: {},
  },
  current: {
    loading: false,
  },
}

// FIXME: do proper typing of this file
export function reducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case MOVE_STEP:
      return {
        ...state,
        step: action.step,
      }
    case ESTIMATION_REQUEST:
    case DELIVERY_UPDATE_REQUEST:
      return {
        ...state,
        estimation: {
          loading: true,
          estimated: false,
          entity: {},
          violations: [],
        },
      }
    case ESTIMATION_SUCCESS:
      return {
        ...state,
        estimation: {
          loading: false,
          estimated: true,
          entity: action.entity,
          violations: [],
        },
        step: 1,
      }
    case DELIVERY_UPDATE_SUCCESS:
      return {
        ...state,
        estimation: {
          loading: false,
          estimated: true,
          entity: action.entity,
          violations: [],
        },
        new: decorateDeliveryResponsentity(action.payload),
        step: 1,
      }
    case ESTIMATION_FAILURE:
    case DELIVERY_UPDATE_FAILURE:
      return {
        ...state,
        estimation: {
          loading: false,
          estimated: false,
          entity: {},
          violations: action.violations,
        },
      }
    case ESTIMATION_RESET:
      return {
        ...state,
        estimation: {
          loading: false,
          estimated: false,
          entity: {},
          violations: [],
        },
      }
    case CREATION_REQUEST:
    case DELIVERY_MODIFY_REQUEST:
      return {
        ...state,
        creation: {
          loading: true,
          created: false,
          entity: {},
          violations: [],
        },
      }
    case CREATION_SUCCESS:
    case DELIVERY_MODIFY_SUCCESS:
      return {
        ...state,
        creation: {
          loading: false,
          created: true,
          entity: action.entity,
          violations: [],
        },
      }
    case CREATION_FAILURE:
    case DELIVERY_MODIFY_FAILURE:
      return {
        ...state,
        creation: {
          loading: false,
          created: false,
          entity: {},
          violations: action.violations,
        },
      }
    case DELETE_PROFITABILITY_REQUEST:
    case CREATE_PROFITABILITY_REQUEST:
    case UPDATE_PROFITABILITY_REQUEST:
      return {
        ...state,
        creation: {
          loading: true,
          created: false,
          entity: {},
          violations: [],
        },
      }
    case CREATE_PROFITABILITY_SUCCESS:
      return {
        ...state,
        creation: {
          loading: false,
          created: true,
          entity: action.entity,
          violations: [],
        },
      }
    case CREATE_PROFITABILITY_FAILURE:
      return {
        ...state,
        creation: {
          loading: false,
          created: false,
          entity: {},
          violations: action.violations,
        },
      }
    case RETRY_REQUEST:
      return {
        ...state,
        retry: {
          loading: true,
          retried: false,
        },
      }
    case RETRY_SUCCESS:
      return {
        ...state,
        retry: {
          loading: false,
          retried: true,
        },
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.entity.id]: action.entity,
          },
        },
      }
    case RETRY_FAILURE:
      return {
        ...state,
        retry: {
          loading: false,
          retried: false,
        },
      }
    case CREATION_RESET:
      return {
        ...state,
        creation: {
          loading: false,
          created: false,
          entity: {},
          violations: [],
        },
      }

    case FETCH_REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
        },
      }

    case FETCH_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          entities: {
            ...state.list.entities,
            [action.entity.id]: {
              ...action.entity,
              events: (state.list.entities[action.entity.id] || {}).events,
            },
          },
        },
        current: {
          ...decorateDeliveryResponsentity(action.entity),
        },
      }

    case FETCH_FAILURE:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
        },
      }

    case CANCEL_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.entity.id]: action.entity,
          },
        },
      }

    case FETCH_ALL_REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
        },
      }
    case FETCH_REFERENCE_REQUEST:
      return {
        ...state,
        list: {
          ...state.list,
          loading: true,
        },
      }
    case FETCH_ALL_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          result: action.result,
          total: action.total,
          entities: {
            ...Object.values(action.entities).reduce(
              (entities, entity) => ({
                ...entities,
                [entity.id]: {
                  ...(state.list.entities[entity.id] || {}),
                  ...entity,
                },
              }),
              {},
            ),
          },
        },
      }
    case FETCH_ALL_FAILURE:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
        },
      }
    case FETCH_PROFITABILITY_REQUEST:
      return {
        ...state,
        profitability: {
          ...state.profitability,
          loading: true,
        },
      }
    case FETCH_PROFITABILITY_SUCCESS:
      return {
        ...state,
        profitability: {
          loading: false,
          entities: action.entities,
        },
      }
    case FETCH_PROFITABILITY_FAILURE:
      return {
        ...state,
        profitability: {
          ...state.profitability,
          loading: false,
        },
      }
    case FETCH_SCHEDULED_REQUEST:
      return {
        ...state,
        scheduled: {
          ...state.scheduled,
          loading: true,
        },
      }
    case FETCH_SCHEDULED_SUCCESS:
      return {
        ...state,
        scheduled: {
          ...state.scheduled,
          loading: false,
          result: action.result,
          entities: {
            ...Object.values(action.entities).reduce(
              (entities, entity) => ({
                ...entities,
                [entity.id]: {
                  ...(state.scheduled.entities[entity.id] || {}),
                  ...entity,
                },
              }),
              {},
            ),
          },
        },
      }
    case FETCH_SCHEDULED_FAILURE:
      return {
        ...state,
        scheduled: {
          ...state.scheduled,
          loading: false,
        },
      }
    case FETCH_SEARCH_REQUEST:
      return {
        ...state,
        search: {
          ...state.search,
          loading: true,
        },
      }
    case FETCH_SEARCH_SUCCESS:
      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          result: action.result,
          entities: {
            ...Object.values(action.entities).reduce(
              (entities, entity) => ({
                ...entities,
                [entity.id]: {
                  ...(state.search.entities[entity.id] || {}),
                  ...entity,
                },
              }),
              {},
            ),
          },
        },
      }
    case FETCH_SEARCH_FAILURE:
      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
        },
      }
    case APPLY_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.entity.id]: action.entity,
          },
        },
      }
    case CONFIRM_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.entity.id]: action.entity,
          },
        },
      }
    case REJECT_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.entity.id]: action.entity,
          },
        },
      }
    case EVENTS_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.id]: {
              ...state.list.entities[action.id],
              events: action.events,
            },
          },
        },
      }
    case CURRENT_RESET:
      return {
        ...state,
        current: {},
      }
    case DELIVERY_MODIFIED_VALUES:
      return {
        ...state,
        current: {
          ...state.current,
          modifiedValues: action.values,
        },
      }
    case DELIVERY_COURIER_TRANSITION_REQUEST:
      return {
        ...state,
      }
    case DELIVERY_COURIER_TRANSITION_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.payload.id]: action.payload,
          },
        },
      }
    case DELIVERY_COURIER_TRANSITION_FAILURE:
      return {
        ...state,
      }

    case APPLY_COURIER_TRANSITION_REQUEST:
      return {
        ...state,
      }
    case APPLY_COURIER_TRANSITION_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          entities: {
            ...state.list.entities,
            [action.payload.id]: action.payload,
          },
        },
      }
    case APPLY_COURIER_TRANSITION_FAILURE:
      return {
        ...state,
      }
    default:
      return state
  }
}

// Actions Creators
export const moveStep = (step) => ({
  type: MOVE_STEP,
  step,
})

export const estimationRequest = (payload) => ({
  type: ESTIMATION_REQUEST,
  payload: parseDeliveryPayload(payload),
})

export const estimationSuccess = (entity) => ({
  type: ESTIMATION_SUCCESS,
  entity,
})

export const estimationFailure = (violations = []) => ({
  type: ESTIMATION_FAILURE,
  violations,
})

export const estimationReset = () => ({
  type: ESTIMATION_RESET,
})

export const creationRequest = (payload) => ({
  type: CREATION_REQUEST,
  payload: parseDeliveryPayload(payload),
})

export const creationSuccess = (entity) => ({
  type: CREATION_SUCCESS,
  entity,
})

export const creationFailure = (violations) => ({
  type: CREATION_FAILURE,
  violations,
})

export const createProfitabilityRequest = (payload) => ({
  type: CREATE_PROFITABILITY_REQUEST,
  payload,
})

export const updateProfitabilityRequest = (payload) => ({
  type: UPDATE_PROFITABILITY_REQUEST,
  payload,
})

export const deleteProfitabilityRequest = (payload) => ({
  type: DELETE_PROFITABILITY_REQUEST,
  payload,
})

export const createProfitabilityFailure = (violations) => ({
  type: CREATE_PROFITABILITY_FAILURE,
  violations,
})

export const retryRequest = (id) => ({
  type: RETRY_REQUEST,
  id,
  payload: {
    transition: 'request_search',
  },
})

export const retrySuccess = (entity) => ({
  type: RETRY_SUCCESS,
  entity,
})

export const retryFailure = () => ({
  type: RETRY_FAILURE,
})

export const creationReset = () => ({
  type: CREATION_RESET,
})

export const cancelRequest = (id, delivery, payload) => ({
  type: CANCEL_REQUEST,
  id,
  delivery,
  payload: {
    ...payload,
    transition: 'cancellation',
  },
})

export const cancelSuccess = (entity) => ({
  type: CANCEL_SUCCESS,
  entity,
})

export const fetchRequest = (id) => ({
  type: FETCH_REQUEST,
  id,
})

export const fetchSuccess = (entity) => ({
  type: FETCH_SUCCESS,
  entity,
})

export const fetchFailure = () => ({
  type: FETCH_FAILURE,
})

export const applyRequest = (
  id,
  transition = 'apply',
  courierStart = null,
  courierFinish = null,
  success = () => {},
  failure = () => {},
  counterOfferCourierPrice = null,
) => ({
  type: APPLY_REQUEST,
  id,
  payload: {
    transition,
    ...(courierStart ? { courierStart: `/api/company_couriers/${courierStart}` } : {}),
    ...(courierFinish ? { courierFinish: `/api/company_couriers/${courierFinish}` } : {}),
    ...(counterOfferCourierPrice ? { counterOfferCourierPrice: counterOfferCourierPrice } : {}),
  },
  success,
  failure,
})

export const applySuccess = (entity) => ({
  type: APPLY_SUCCESS,
  entity,
})

export const applyFailure = () => ({
  type: APPLY_FAILURE,
})

export const confirmRequest = (id, transition = 'confirmation', emptyReturn = null) => ({
  type: CONFIRM_REQUEST,
  id,
  payload: {
    transition,
    ...(emptyReturn ? { emptyReturnRide: `/api/rides/${emptyReturn.value}` } : {}),
  },
})

export const confirmSuccess = (entity) => ({
  type: CONFIRM_SUCCESS,
  entity,
})

export const confirmFailure = () => ({
  type: CONFIRM_FAILURE,
})

export const rejectRequest = (id, transition = 'rejection') => ({
  type: REJECT_REQUEST,
  id,
  payload: {
    transition,
  },
})

export const rejectSuccess = (entity) => ({
  type: REJECT_SUCCESS,
  entity,
})

export const rejectFailure = () => ({
  type: REJECT_FAILURE,
})

export const fetchReferenceRequest = (payload) => {
  return {
    type: FETCH_REFERENCE_REQUEST,
    payload: payload,
  }
}

export const fetchAllRequest = (itemsPerPage, pageNumber, statuses) => ({
  type: FETCH_ALL_REQUEST,
  itemsPerPage,
  pageNumber,
  statuses,
})

export const fetchAllSuccess = (result) => {
  const items = result['hydra:member'].map((entity) => entity)
  const entities = items.reduce((obj, delivery) => ({ ...obj, [delivery.id]: delivery }), {})
  return {
    type: FETCH_ALL_SUCCESS,
    result: Object.keys(entities),
    total: result['hydra:totalItems'],
    entities,
  }
}

export const fetchAllFailure = () => ({
  type: FETCH_ALL_FAILURE,
})

export const fetchProfitabilityRequest = (startDate = null, endDate = null, licenseNumbers = null) => ({
  type: FETCH_PROFITABILITY_REQUEST,
  startDate,
  endDate,
  licenseNumbers,
})

export const fetchProfitabilitySuccess = (result) => {
  const entities = result['hydra:member']
  return {
    type: FETCH_PROFITABILITY_SUCCESS,
    entities,
  }
}

export const fetchProfitabilityFailure = () => ({
  type: FETCH_PROFITABILITY_FAILURE,
})

export const fetchScheduledRequest = (params) => ({
  type: FETCH_SCHEDULED_REQUEST,
  params,
})

export const fetchScheduledSuccess = (result) => {
  const entities = result.reduce((obj, delivery) => ({ ...obj, [delivery.id]: delivery }), {})

  return {
    type: FETCH_SCHEDULED_SUCCESS,
    result: Object.keys(entities),
    entities,
  }
}

export const fetchScheduledFailure = () => ({
  type: FETCH_SCHEDULED_FAILURE,
})

export const fetchSearchRequest = () => ({
  type: FETCH_SEARCH_REQUEST,
})

export const fetchSearchSuccess = (result) => {
  const entities = result.reduce((obj, delivery) => ({ ...obj, [delivery.id]: delivery }), {})

  return {
    type: FETCH_SEARCH_SUCCESS,
    result: Object.keys(entities),
    entities,
  }
}

export const fetchSearchFailure = () => ({
  type: FETCH_SEARCH_FAILURE,
})

export const eventsRequest = (id) => ({
  type: EVENTS_REQUEST,
  id,
})

export const eventsSuccess = (id, events) => ({
  type: EVENTS_SUCCESS,
  id,
  events,
})

export const eventsFailure = () => ({
  type: EVENTS_FAILURE,
})

export const openModal = () => ({
  type: APPLY_MODAL_OPEN,
})

export const closeModal = () => ({
  type: APPLY_MODAL_CLOSE,
})

export const currentDeliveryReset = () => ({
  type: CURRENT_RESET,
})

export const deliveryUpdateRequest = (payload) => ({
  type: DELIVERY_UPDATE_REQUEST,
  payload,
})

export const deliveryUpdateRequestSuccess = (payload) => ({
  type: DELIVERY_UPDATE_SUCCESS,
  payload,
})

export const deliveryUpdateRequestFailure = () => ({
  type: DELIVERY_UPDATE_FAILURE,
})

export const deliveryModifyRequest = (payload) => ({
  type: DELIVERY_MODIFY_REQUEST,
  payload,
})

export const deliveryModifyRequestSuccess = (entity) => ({
  type: DELIVERY_MODIFY_SUCCESS,
  entity,
})

export const deliveryModifyRequestFailure = () => ({
  type: DELIVERY_MODIFY_FAILURE,
})

export const deliveryModifiedValues = (values) => ({
  type: DELIVERY_MODIFIED_VALUES,
  values,
})

export const deliveryCourierTransitionRequest = (delivery, transition, destinationState) => ({
  type: DELIVERY_COURIER_TRANSITION_REQUEST,
  payload: {
    delivery,
    transition,
    destinationState,
  },
})

export const deliveryCourierTransitionSuccess = (payload) => ({
  type: DELIVERY_COURIER_TRANSITION_SUCCESS,
  payload,
})

export const deliveryCourierTransitionFailure = ({ delivery }) => ({
  type: DELIVERY_COURIER_TRANSITION_FAILURE,
  payload: { delivery },
})

export const applyCourierTransitionRequest = (delivery, transition, body) => ({
  type: APPLY_COURIER_TRANSITION_REQUEST,
  payload: {
    delivery,
    transition,
    body,
  },
})

export const applyCourierTransitionSuccess = (payload) => ({
  type: APPLY_COURIER_TRANSITION_SUCCESS,
  payload,
})

export const applyCourierTransitionFailure = ({ delivery }) => ({
  type: APPLY_COURIER_TRANSITION_FAILURE,
  payload: { delivery },
})

// Epics
export const epic = combineEpics(
  estimationRequestEpic,
  creationRequestEpic,
  createProfitabilityRequestEpic,
  updateProfitabilityRequestEpic,
  deleteProfitabilityRequestEpic,
  retryRequestEpic,
  cancelRequestEpic,
  fetchRequestEpic,
  fetchAllRequestEpic,
  fetchProfitabilityRequestEpic,
  fetchScheduledRequestEpic,
  fetchSearchRequestEpic,
  applyRequestEpic,
  confirmRequestEpic,
  rejectRequestEpic,
  eventsRequestEpic,
  fetchByReferenceRequestEpic,
  updateRequestEpic,
  modifyRequestEpic,
  deliveryCourierTransitionEpic,
  applyCourierTransitionEpic,
)

export function estimationRequestEpic(action$) {
  return action$.ofType(ESTIMATION_REQUEST).mergeMap((action) =>
    Observable.ajax(estimate({ payload: action.payload }))
      .map((res) => estimationSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(estimationFailure(errors))
        } catch (e) {
          return Observable.of(estimationFailure())
        }
      }),
  )
}

export function creationRequestEpic(action$) {
  return action$.ofType(CREATION_REQUEST).mergeMap((action) =>
    Observable.ajax(create({ payload: action.payload }))
      .map((res) => creationSuccess(res.response))
      // @ts-expect-error
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        try {
          const errors = [...(err.response.violations || []), ...(err.response.errors || [])].map((e) =>
            typeof e === 'string' ? e : e.message,
          )
          return Observable.of(creationFailure(errors))
        } catch (e) {
          // @ts-expect-error
          return Observable.of(creationFailure())
        }
      }),
  )
}

export function createProfitabilityRequestEpic(action$) {
  return action$.ofType(CREATE_PROFITABILITY_REQUEST).mergeMap((action) =>
    Observable.ajax(createProfitability({ payload: action.payload }))
      .mapTo(fetchProfitabilityRequest() as AnyAction)
      .catch((err) => {
        if (err.response.code === 401) {
          return of(authenticationDuck.refreshTokenRequest(action) as AnyAction)
        }
        const errors = [...(err.response.violations ?? []), ...(err.response.errors ?? [])].map((e) =>
          typeof e === 'string' ? e : e.message,
        )
        return of(createProfitabilityFailure(errors) as AnyAction)
      }),
  )
}

export function updateProfitabilityRequestEpic(action$) {
  return action$.ofType(UPDATE_PROFITABILITY_REQUEST).mergeMap((action) =>
    Observable.ajax(updateProfitability({ payload: action.payload }))
      .mapTo(fetchProfitabilityRequest())
      .catch((err) =>
        Observable.of({
          type: 'UPDATE_PROFITABILITY_FAILURE',
          error: true,
          payload: err,
        }),
      ),
  )
}

export function deleteProfitabilityRequestEpic(action$) {
  return action$.ofType(DELETE_PROFITABILITY_REQUEST).mergeMap((action) =>
    Observable.ajax(deleteProfitability({ payload: action.payload }))
      .mapTo(fetchProfitabilityRequest())
      .catch((err) =>
        Observable.of({
          type: 'DELETE_PROFITABILITY_FAILURE',
          error: true,
          payload: err,
        }),
      ),
  )
}

export function retryRequestEpic(action$) {
  return action$.ofType(RETRY_REQUEST).mergeMap((action) =>
    Observable.ajax(transition({ params: { id: action.id }, payload: action.payload }))
      .mergeMap((res) =>
        Observable.of(
          retrySuccess(res.response),
          // @ts-expect-error
          alertSuccess(intl.formatMessage({ id: 'ducks.delivery.retry.success' })),
        ),
      )
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(retryFailure(), alertError(intl.formatMessage({ id: 'ducks.delivery.retry.failure' })))
      }),
  )
}

export function cancelRequestEpic(action$) {
  return action$.ofType(CANCEL_REQUEST).mergeMap((action) =>
    Observable.ajax(transition({ params: { id: action.id }, payload: action.payload }))
      .mergeMap((res) =>
        Observable.of(
          cancelSuccess(res.response),
          // @ts-expect-error
          alertSuccess(intl.formatMessage({ id: 'ducks.delivery.cancel.success' }, action.delivery)),
        ),
      )
      // @ts-expect-error
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(alertError(intl.formatMessage({ id: 'ducks.delivery.cancel.failure' }, action.delivery)))
      }),
  )
}

export function fetchRequestEpic(action$) {
  return action$.ofType(FETCH_REQUEST).mergeMap((action) =>
    Observable.ajax(get({ params: { id: action.id } }))
      .map((res) => fetchSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchFailure())
      }),
  )
}

export function fetchAllRequestEpic(action$) {
  return action$.ofType(FETCH_ALL_REQUEST).mergeMap((action) =>
    Observable.ajax(
      all({ itemsPerPage: action.itemsPerPage, pageNumber: action.pageNumber, statuses: action.statuses }),
    )
      .map((res) => fetchAllSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchAllFailure())
      }),
  )
}

export function fetchProfitabilityRequestEpic(action$) {
  return action$.ofType(FETCH_PROFITABILITY_REQUEST).mergeMap((action) =>
    Observable.ajax(
      profitability({ startDate: action.startDate, endDate: action.endDate, licenseNumbers: action.licenseNumbers }),
    )
      .map((res) => fetchProfitabilitySuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchProfitabilityFailure())
      }),
  )
}

export function fetchByReferenceRequestEpic(action$) {
  return action$.ofType(FETCH_REFERENCE_REQUEST).mergeMap((action) =>
    Observable.ajax(reference(action.payload))
      .map((res) => fetchAllSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchAllFailure())
      }),
  )
}

export function fetchSearchRequestEpic(action$) {
  return action$.ofType(FETCH_SEARCH_REQUEST).mergeMap((action) =>
    Observable.ajax(search())
      .map((res) => fetchSearchSuccess(res.response.map((entity) => entity)))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }

        return Observable.of(fetchSearchFailure())
      }),
  )
}

export function fetchScheduledRequestEpic(action$) {
  return action$.ofType(FETCH_SCHEDULED_REQUEST).mergeMap((action) =>
    Observable.ajax(scheduled(action.params))
      .map((res) => fetchScheduledSuccess(res.response['hydra:member'].map((entity) => entity)))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }
        return Observable.of(fetchScheduledFailure())
      }),
  )
}

export function applyRequestEpic(action$, store) {
  return action$.ofType(APPLY_REQUEST).mergeMap((action) =>
    Observable.ajax(
      transition(
        {
          params: {
            id: action.id,
          },
          payload: action.payload,
        },
        store.getState(),
      ),
    )
      .map((res) => applySuccess(res.response))
      .do((res) => (action.success || (() => {}))(action, res))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }
        // @ts-expect-error
        return Observable.of(applyFailure(false)).do(() => (action.failure || (() => {}))(action))
      }),
  )
}

export function confirmRequestEpic(action$, store) {
  return action$.ofType(CONFIRM_REQUEST).mergeMap((action) =>
    Observable.ajax(
      transition(
        {
          params: {
            id: action.id,
          },
          payload: action.payload,
        },
        store.getState(),
      ),
    )
      .mergeMap((res) =>
        Observable.of(
          confirmSuccess(res.response),
          // @ts-expect-error
          alertSuccess(intl.formatMessage({ id: 'ducks.delivery.confirm.success' })),
        ),
      )
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }
        // @ts-expect-error
        return Observable.of(confirmFailure(false))
      }),
  )
}

export function rejectRequestEpic(action$, store) {
  return action$.ofType(REJECT_REQUEST).mergeMap((action) =>
    Observable.ajax(
      transition(
        {
          params: {
            id: action.id,
          },
          payload: action.payload,
        },
        store.getState(),
      ),
    )
      .map((res) => rejectSuccess(res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }
        // @ts-expect-error
        return Observable.of(rejectFailure(false))
      }),
  )
}

export function eventsRequestEpic(action$, store) {
  return action$.ofType(EVENTS_REQUEST).mergeMap((action) =>
    Observable.ajax(
      events(
        {
          params: {
            id: action.id,
          },
          payload: action.payload,
        },
        store.getState(),
      ),
    )
      .map((res) => eventsSuccess(action.id, res.response))
      .catch((err) => {
        if (typeof err === 'object' && (err.response || {}).code === 401) {
          return Observable.of(authenticationDuck.refreshTokenRequest(action))
        }
        // @ts-expect-error
        return Observable.of(eventsFailure(false))
      }),
  )
}

export function updateRequestEpic(action$) {
  return action$.ofType(DELIVERY_UPDATE_REQUEST).mergeMap((action) =>
    Observable.ajax(
      validation({
        params: {
          id: action.payload.id,
        },
        payload: parseDeliveryUpdatePayload(action.payload),
      }),
    )
      .map((res) => deliveryUpdateRequestSuccess(res.response))
      .catch(() => {
        return Observable.of(alertError("La valeur n'a pas pu être modifiée"), deliveryUpdateRequestFailure())
      }),
  )
}

export function modifyRequestEpic(action$) {
  return action$.ofType(DELIVERY_MODIFY_REQUEST).mergeMap((action) =>
    Observable.ajax(
      modify({
        params: {
          id: action.payload.id,
        },
        payload: parseDeliveryUpdatePayload(action.payload),
      }),
    )
      .map((res) => deliveryModifyRequestSuccess(res.response))
      .catch(() => {
        return Observable.of(alertError("La valeur n'a pas pu être modifiée"), deliveryModifyRequestFailure())
      }),
  )
}

export const getDeliveriesByFilters = (state, filters) =>
  Object.values(state.delivery.list.entities)
    .filter((entity) => filters['statuses'].includes(entity.operatorState))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((entity) => decorateDeliveryEntity(entity, state))

getDeliveriesByFilters.propTypes = {
  filters: PropTypes.shape({
    statuses: PropTypes.array.isRequired,
  }),
}
export const allDeliveriesSelector = (state) =>
  Object.values(state.delivery.list.entities).map((entity) => decorateDeliveryEntity(entity, state))

export const scheduledSelector = (state) =>
  Object.values(state.delivery.scheduled.entities).map((entity) => decorateDeliveryEntity(entity, state))

export const searchSelector = (state) =>
  Object.values(state.delivery.search.entities).map((companyCourier) => ({
    ...companyCourier,
    availableDeliveries: (companyCourier.availableDeliveries || []).map((delivery) =>
      decorateDeliveryEntity(delivery, state),
    ),
  }))

export function deliveryCourierTransitionEpic(action$) {
  return action$.ofType(DELIVERY_COURIER_TRANSITION_REQUEST).mergeMap((action) =>
    Observable.ajax(operatorCourierTransition(action.payload.delivery.id, action.payload.transition))
      .map((res) => deliveryCourierTransitionSuccess(res.response))
      .catch(() => {
        return Observable.of(
          alertError("La valeur n'a pas pu être modifiée"),
          // @ts-expect-error
          deliveryCourierTransitionFailure(action.payload.delivery),
        )
      }),
  )
}

export function applyCourierTransitionEpic(action$) {
  return action$.ofType(APPLY_COURIER_TRANSITION_REQUEST).mergeMap((action) =>
    Observable.ajax(applyCourierTransition(action.payload.delivery.id, action.payload.transition, action.payload.body))
      .map((res) => applyCourierTransitionSuccess(res.response))
      .catch(() => {
        return Observable.of(
          alertError("La valeur n'a pas pu être modifiée"),
          // @ts-expect-error
          applyCourierTransitionFailure(action.payload.delivery),
        )
      }),
  )
}

export const selectEstimation = (state) => state.delivery.estimation
export const selectCreation = (state) => state.delivery.creation
